/** Row shape from GET user/program/:id/institute_courses (see PaymentServices types). */
export type UserProgramInstituteCourseRow = {
  user_id?: string;
  institute_id?: string;
  institute_course_id?: string | null;
  accommodation_mode?: string;
  registered_at?: string;
};

/**
 * API returns `registered_at` as `YYYY-MM-DD HH:mm:ss` (space-separated).
 */
export const parseRegisteredAtToTimestamp = (
  registeredAt: string | undefined
): number => {
  if (!registeredAt?.trim()) {
    return 0;
  }
  const normalized = registeredAt.trim().replace(' ', 'T');
  const ms = Date.parse(normalized);
  return Number.isNaN(ms) ? 0 : ms;
};

export const getLatestUserProgramInstituteCourseRow = (
  rows: UserProgramInstituteCourseRow[] | undefined | null
): UserProgramInstituteCourseRow | null => {
  if (!rows?.length) {
    return null;
  }
  return [...rows].sort(
    (a, b) =>
      parseRegisteredAtToTimestamp(b.registered_at) -
      parseRegisteredAtToTimestamp(a.registered_at)
  )[0];
};

const getAmountFromLeastSubunit = (value: unknown): number | null => {
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return value / 100;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsedValue = Number(value);
    if (!Number.isNaN(parsedValue)) {
      return parsedValue / 100;
    }
  }
  return null;
};

const parseLeastSubunitAmount = (value: unknown): number | null => {
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsedValue = Number(value);
    if (!Number.isNaN(parsedValue)) {
      return parsedValue;
    }
  }
  return null;
};

export const isOrderDetailsFullyPaid = (
  details:
    | {
        amount_paid_in_least_subunit?: unknown;
        amount_to_be_paid_in_least_subunit?: unknown;
      }
    | null
    | undefined
): boolean => {
  if (!details) {
    return false;
  }
  const amountPaid = parseLeastSubunitAmount(
    details.amount_paid_in_least_subunit
  );
  const amountToBePaid = parseLeastSubunitAmount(
    details.amount_to_be_paid_in_least_subunit
  );
  if (amountPaid === null || amountPaid <= 0) {
    return false;
  }
  if (amountToBePaid !== null && amountToBePaid > 0) {
    return amountPaid >= amountToBePaid;
  }
  return true;
};

export const parseJsonSafe = (input: unknown): Record<string, unknown> => {
  if (typeof input !== 'string' || input.trim() === '') {
    return {};
  }
  try {
    return JSON.parse(input) as Record<string, unknown>;
  } catch {
    return {};
  }
};

type GetUserOrdersDetailsApi = () => Promise<{
  user_orders_details?: Array<{
    program?: string;
    registered?: Array<{
      product_code?: string;
      user_order_details?: {
        order_id?: string;
        amount_paid_in_least_subunit?: unknown;
        amount_to_be_paid_in_least_subunit?: unknown;
        extra_info_based_on_product_config?: string;
      };
    }>;
    expired?: Array<{
      product_code?: string;
      user_order_details?: {
        order_id?: string;
        amount_paid_in_least_subunit?: unknown;
        amount_to_be_paid_in_least_subunit?: unknown;
        extra_info_based_on_product_config?: string;
      };
    }>;
  }>;
}>;

type GetOrderStatusApi = (args: { orderId: string }) => Promise<unknown>;

export type SeatReservationOrderExtras = {
  amountPaidRupee: number | null;
  preferredCollegeLabel: string;
};

/**
 * Seat reservation paid amount + college label from order / order-status (same sources as EnrollmentSuccessPage).
 */
export const fetchSeatReservationOrderExtras = async (params: {
  getUserOrdersDetailsApi: GetUserOrdersDetailsApi;
  getOrderStatusApi: GetOrderStatusApi;
  seatProductCode: string;
}): Promise<SeatReservationOrderExtras> => {
  const { getUserOrdersDetailsApi, getOrderStatusApi, seatProductCode } =
    params;
  if (!seatProductCode) {
    return { amountPaidRupee: null, preferredCollegeLabel: '' };
  }

  const userOrdersResponse = await getUserOrdersDetailsApi();
  const userOrdersDetails = userOrdersResponse?.user_orders_details ?? [];
  const niatProgramOrders = userOrdersDetails.find(
    (orderDetail) => orderDetail?.program === 'NIAT'
  );

  const pickOrder = (productCode: string) => {
    return (
      niatProgramOrders?.registered?.find(
        (orderDetail) => orderDetail?.product_code === productCode
      ) ??
      niatProgramOrders?.expired?.find(
        (orderDetail) => orderDetail?.product_code === productCode
      ) ??
      null
    );
  };

  const seatOrder = pickOrder(seatProductCode);
  if (!seatOrder?.user_order_details) {
    return { amountPaidRupee: null, preferredCollegeLabel: '' };
  }

  const orderDetail = seatOrder.user_order_details;
  const orderId = orderDetail?.order_id ?? '';
  let orderStatusResponse: unknown = null;
  if (orderId) {
    try {
      orderStatusResponse = await getOrderStatusApi({ orderId });
    } catch {
      orderStatusResponse = null;
    }
  }

  const orderStatusPayload =
    (orderStatusResponse as { response?: { data?: Record<string, unknown> } })
      ?.response?.data ??
    (orderStatusResponse as { data?: Record<string, unknown> })?.data ??
    (orderStatusResponse as Record<string, unknown>) ??
    {};

  const extraInfoFromOrderStatus = parseJsonSafe(
    orderStatusPayload?.extra_info_based_on_product_config as string
  );
  const extraInfoFromUserOrder = parseJsonSafe(
    orderDetail?.extra_info_based_on_product_config as string | undefined
  );

  const amountPaid = getAmountFromLeastSubunit(
    orderStatusPayload?.amount ??
      orderStatusPayload?.amount_paid ??
      orderDetail?.amount_paid_in_least_subunit ??
      orderDetail?.amount_to_be_paid_in_least_subunit
  );

  const preferredCollegeLabel = String(
    extraInfoFromOrderStatus.preferredUniversity ??
      extraInfoFromOrderStatus.preferred_college_university ??
      extraInfoFromUserOrder.preferredUniversity ??
      extraInfoFromUserOrder.preferred_college_university ??
      ''
  );

  return {
    amountPaidRupee: amountPaid,
    preferredCollegeLabel
  };
};

type EnrollmentFeeCourseProductRow = {
  product_code?: string;
  product_purpose?: string;
};

/** Institute slice used to resolve ENROLLMENT_FEE product codes for paid-order checks. */
export type InstituteWithCoursesForEnrollmentProducts = {
  courses?: Array<{
    course_id?: string;
    course_name?: string;
    products_details?: EnrollmentFeeCourseProductRow[];
    product_details?: EnrollmentFeeCourseProductRow[];
    remaining_seat_count?: number | null;
  }>;
};

const courseHasEnrollmentFeeProduct = (
  course: NonNullable<
    InstituteWithCoursesForEnrollmentProducts['courses']
  >[number]
): boolean => {
  const productRows: EnrollmentFeeCourseProductRow[] =
    course?.products_details ?? course?.product_details ?? [];
  return productRows.some(
    (p) =>
      String(p.product_purpose ?? '').toUpperCase() === 'ENROLLMENT_FEE' &&
      Boolean(String(p.product_code ?? '').trim())
  );
};

/**
 * Courses that expose an ENROLLMENT_FEE product in catalog (`products_details` / `product_details`).
 * Used to drive enrollment pay UI vs "all set" — not raw course count.
 */
export const getCoursesWithEnrollmentFeeProduct = (
  institute: InstituteWithCoursesForEnrollmentProducts | null | undefined
): NonNullable<InstituteWithCoursesForEnrollmentProducts['courses']> => {
  if (!institute?.courses?.length) {
    return [];
  }
  return institute.courses.filter(
    (c) => Boolean(c.course_id) && courseHasEnrollmentFeeProduct(c)
  );
};

/**
 * Collects distinct ENROLLMENT_FEE product codes for the institute.
 * When `instituteCourseId` is set, only that course is considered (if it has an enrollment-fee product); otherwise all courses that do.
 */
export const collectEnrollmentFeeProductCodes = (
  institute: InstituteWithCoursesForEnrollmentProducts | null | undefined,
  instituteCourseId?: string | null
): string[] => {
  let courses = getCoursesWithEnrollmentFeeProduct(institute);
  if (instituteCourseId) {
    courses = courses.filter((c) => c.course_id === instituteCourseId);
  }
  const codes = new Set<string>();
  for (const course of courses) {
    const productRows: EnrollmentFeeCourseProductRow[] =
      course.products_details ?? course.product_details ?? [];
    const match = productRows.find(
      (p) => String(p.product_purpose ?? '').toUpperCase() === 'ENROLLMENT_FEE'
    );
    if (match?.product_code) {
      codes.add(match.product_code);
    }
  }
  return Array.from(codes);
};

export const getWhatsappSupportUrl = (
  sectionDetailsInfoTabsConfig: any[] | undefined,
  sectionNameEnum: string
): string | null => {
  const extraConfigRaw =
    sectionDetailsInfoTabsConfig?.find(
      (section) => section.id === sectionNameEnum
    )?.extraConfig ?? '';
  const parsed = parseJsonSafe(extraConfigRaw);
  return typeof parsed?.whatsapp_support_url === 'string'
    ? parsed.whatsapp_support_url
    : null;
};

/**
 * Returns `true` when a course has remaining seats available for selection.
 * A course is selectable when `remaining_seat_count` is undefined (field absent)
 * or greater than 0. Only an explicit `0` hides the course.
 */
export const isCourseSeatSelectable = (
  course: Pick<
    NonNullable<InstituteWithCoursesForEnrollmentProducts['courses']>[number],
    'remaining_seat_count'
  >
): boolean => {
  return course.remaining_seat_count !== 0;
};

/**
 * Filters an institutes list to only those that have at least one course
 * with remaining seats available. Institutes where every course has
 * `remaining_seat_count === 0` are excluded from the result.
 */
export const getInstitutesWithSelectableCourses = <
  T extends { courses?: Array<{ remaining_seat_count?: number | null }> }
>(
  institutes: T[]
): T[] => {
  return institutes.filter(
    (inst) =>
      inst.courses?.some((course) => isCourseSeatSelectable(course)) ?? false
  );
};

/**
 * Returns courses that are safe to show in the enrollment course picker:
 * must have an ENROLLMENT_FEE product AND have remaining seats available.
 * Raw course data (including zero-seat courses) is intentionally preserved
 * in machine context for product-code resolution and locked-course display.
 */
export const getEnrollmentCoursePickerOptions = (
  institute: InstituteWithCoursesForEnrollmentProducts | null | undefined
): NonNullable<InstituteWithCoursesForEnrollmentProducts['courses']> => {
  return getCoursesWithEnrollmentFeeProduct(institute).filter((course) =>
    isCourseSeatSelectable(course)
  );
};
